// Runs a SAS program through the wasm evaluator (kolistat.run) and
// streams the resulting log envelope into the output panel. Tables
// stay JS-side in DuckDB-wasm — the evaluator emits text tables via
// the legacy formatter, so the panel mirrors what the native CLI
// would print. Errors don't stop execution; the evaluator logs and
// continues, matching native behaviour.
//
// Plots (VISUALIZE / PROC SGPLOT) arrive out-of-band during the run
// via the plot sink in visualize.ts; runProgram buffers them and
// flushes AFTER the run's text output, in execution order.

import type { Kolilang } from './wasm';
import { resetSession } from './duckdb';
import { setPlotSink, type PlotData } from './visualize';
import { prepareRun } from './data-files';

export interface OutputPanel {
  clear(): void;
  log(text: string): void;
  error(text: string): void;
  /// Structured table render. Unused by the run path today (eval
  /// produces text tables); kept on the interface so a future "show
  /// dataset" affordance can reuse it.
  table(columns: string[], rows: unknown[][]): void;
  /// Vega-Lite plot block (stats_duck VISUALIZE output). Renders a
  /// framed figure with a mono caption; the chart machinery loads
  /// lazily on first use. clear() destroys mounted charts.
  plot(spec: unknown, datasets: Record<string, unknown[]>): void;
}

const NOTE_RE = /^\s*NOTE:/i;
const WARN_RE = /^\s*WARNING:/i;
const ERR_RE = /^\s*ERROR:/i;

// Stable marker for the engine's entitlement gate. The engine emits
// "ERROR: PROC <NAME> is not available yet. Create a free account…"
// when a run carries no licence context, and only this gate uses that
// phrase, so a substring match is enough to tell it from any other
// ERROR line. The editor sends a licensed context for every signed-in
// user and checks the sign-in before each run, so the gate can only
// fire when the session lapsed between that check and the run. A
// structured ABI flag would be sturdier but is an ABI change.
const PAID_PROC_GATE_RE = /is not available yet/;

function logClass(text: string): string {
  if (ERR_RE.test(text)) return 'out-err';
  if (WARN_RE.test(text)) return 'out-warn';
  if (NOTE_RE.test(text)) return 'out-note';
  return 'out-log';
}

export function attachOutputPanel(host: HTMLElement): OutputPanel {
  const scrollToEnd = (): void => { host.scrollTop = host.scrollHeight; };
  const appendLine = (cls: string, text: string): void => {
    const pre = document.createElement('pre');
    pre.className = cls;
    pre.textContent = text;
    host.appendChild(pre);
    scrollToEnd();
  };
  // Mounted ChartVisualizers, destroyed on clear() — vega views hold
  // rAF loops + resize listeners that would otherwise leak run-over-run.
  const charts: Array<{ destroy(): void }> = [];
  let plotCount = 0;
  return {
    clear: () => {
      for (const c of charts.splice(0)) {
        try { c.destroy(); } catch { /* chart already torn down */ }
      }
      plotCount = 0;
      host.innerHTML = '';
    },
    log: (text) => appendLine(logClass(text), text),
    error: (text) => appendLine('out-err', text),
    table: (columns, rows) => {
      const table = document.createElement('table');
      table.className = 'out-table';
      const thead = document.createElement('thead');
      const hr = document.createElement('tr');
      for (const c of columns) {
        const th = document.createElement('th');
        th.textContent = c;
        hr.appendChild(th);
      }
      thead.appendChild(hr);
      table.appendChild(thead);
      const tbody = document.createElement('tbody');
      for (const row of rows) {
        const tr = document.createElement('tr');
        for (const cell of row) {
          const td = document.createElement('td');
          td.textContent = cell == null ? '.' : String(cell);
          tr.appendChild(td);
        }
        tbody.appendChild(tr);
      }
      table.appendChild(tbody);
      host.appendChild(table);
      scrollToEnd();
    },
    plot: (spec, datasets) => {
      plotCount += 1;
      const n = plotCount;
      const fig = document.createElement('figure');
      fig.className = 'out-plot';
      const chartHost = document.createElement('div');
      chartHost.className = 'out-plot-chart';
      const caption = document.createElement('figcaption');
      caption.className = 'out-plot-caption';
      caption.textContent = `plot ${n}`;
      fig.appendChild(chartHost);
      fig.appendChild(caption);
      host.appendChild(fig);
      scrollToEnd();
      // By the time a plot reaches the panel the chart chunk is already
      // loaded (the bridge used it to produce the spec), so this import
      // resolves from cache.
      void import('./chart')
        .then((m) => m.mountChart(chartHost, spec, datasets))
        .then((chart) => {
          charts.push(chart);
          scrollToEnd();
        })
        .catch((e) => {
          caption.classList.add('out-plot-failed');
          caption.textContent = `plot ${n} — render failed: ${e instanceof Error ? e.message : String(e)}`;
        });
    },
  };
}

// The runs in flight, if any. A reset while a run is still registering
// files or executing would kill the worker under an in-flight request,
// which duckdb-wasm never settles; the reset waits for every run instead.
const inFlight = new Set<Promise<void>>();

export function isRunInFlight(): boolean {
  return inFlight.size > 0;
}

let resetInFlight: Promise<void> | null = null;

/// Drop the in-browser DuckDB session — used by the toolbar's reset
/// button and by the tutorial mode between lessons. Concurrent calls
/// share one reset instead of racing resetSession() against itself.
/// Re-establishing the connection happens lazily on the next
/// kolistat.run call.
export async function resetRunSession(): Promise<void> {
  if (resetInFlight) return resetInFlight;
  resetInFlight = (async () => {
    await Promise.allSettled([...inFlight]);
    await resetSession();
  })();
  try {
    await resetInFlight;
  } finally {
    resetInFlight = null;
  }
}

export function runProgram(
  kolistat: Kolilang,
  source: string,
  out: OutputPanel,
  // Fired once per run when the engine's gate refused a procedure (see
  // PAID_PROC_GATE_RE), which means the session lapsed between the
  // sign-in check and the run. main.ts owns the response (the sign-in
  // dialog); the runner stays UI-agnostic. The engine's own message
  // still renders in the panel via emitLines.
  onRunBlocked?: () => void,
): Promise<void> {
  const run = runProgramInner(kolistat, source, out, onRunBlocked).finally(() => {
    inFlight.delete(run);
  });
  inFlight.add(run);
  return run;
}

async function runProgramInner(
  kolistat: Kolilang,
  source: string,
  out: OutputPanel,
  onRunBlocked?: () => void,
): Promise<void> {
  out.clear();
  // Fresh buffer per run; the sink is cleared in the finally so plots
  // can never leak into a later run's panel.
  const plots: PlotData[] = [];
  setPlotSink((p) => plots.push(p));
  // Data files the program names must be registered with DuckDB before
  // the engine runs. A failed load is reported and the run goes ahead,
  // so DuckDB's own "file not found" line still tells the user which
  // path it looked for.
  try {
    await prepareRun(source);
  } catch (e) {
    out.error(`data file load failed: ${e instanceof Error ? e.message : String(e)}`);
  }
  let result;
  try {
    result = await kolistat.run(source);
  } catch (e) {
    out.error(`runner failed: ${e instanceof Error ? e.message : String(e)}`);
    return;
  } finally {
    setPlotSink(null);
  }
  for (const d of result.diagnostics) {
    out.error(`ERROR (${d.s}-${d.e}): ${d.msg}`);
  }
  emitLines(result.out, out, false);
  emitLines(result.err, out, true);
  // Plots render after the run's full text output, in execution order.
  for (const p of plots) {
    out.plot(p.spec, p.datasets);
  }
  if (onRunBlocked && PAID_PROC_GATE_RE.test(result.err)) {
    onRunBlocked();
  }
}

function emitLines(text: string, out: OutputPanel, asError: boolean): void {
  if (!text) return;
  // Eval streams already-formatted lines (the LegacyFormatter
  // terminates with '\n'). Split and drop the trailing empty so we
  // don't render a phantom blank line, but preserve internal blanks
  // — PROC PRINT separators rely on them.
  const lines = text.split('\n');
  if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
  for (const line of lines) {
    if (asError) out.error(line);
    else out.log(line);
  }
}
